import type { ShouldRevalidateFunctionArgs } from 'react-router';

/**
 * Search params written only by the booking dialog for its own state — `from`
 * (date anchor), `eventId` (pre-selected event), `t` (ticket quantities). No
 * page loader reads these, so parent routes can skip revalidating when they
 * are the only thing that changed.
 */
export const BOOKING_DIALOG_SELECTION_PARAMS = ['from', 'eventId', 't'];

/**
 * The dialog's filter params. These share their vocabulary with the
 * experience page's booking widget, whose loader DOES read them — the
 * experience route must keep revalidating when they change, so it passes
 * only `BOOKING_DIALOG_SELECTION_PARAMS` to the helper below.
 */
export const BOOKING_DIALOG_FILTER_PARAMS = ['language', 'locationId'];

export const ALL_BOOKING_DIALOG_PARAMS = [
  ...BOOKING_DIALOG_SELECTION_PARAMS,
  ...BOOKING_DIALOG_FILTER_PARAMS,
];

/**
 * The booking dialog mounts as a child route (`…/book` on the experience
 * page, `…/info/edit` in checkout) and keeps all of its state in URL search
 * params. Every open/close and in-dialog change is therefore a navigation
 * with a changed search string — which by default revalidates EVERY matched
 * parent loader, so opening the dialog waits on the (expensive) page loader
 * re-running for data it already has.
 *
 * This helper implements a `shouldRevalidate` that skips exactly that case:
 * a navigation staying on the same page (the dialog child segment aside)
 * where the search params — ignoring `ignoredParams` — are unchanged.
 * Everything else (mutations, same-URL revalidations like polling, moving to
 * a different page, or a non-ignored param changing) keeps React Router's
 * default behavior.
 */
export function shouldRevalidateIgnoringBookingDialogParams(
  args: ShouldRevalidateFunctionArgs,
  ignoredParams: readonly string[] = ALL_BOOKING_DIALOG_PARAMS
): boolean {
  const { currentUrl, nextUrl, formMethod, defaultShouldRevalidate } = args;

  // Submissions can change data any loader depends on (e.g. the basket).
  if (formMethod) return defaultShouldRevalidate;

  // Same-URL revalidations (useRevalidator polling, cookie-triggered
  // revalidation) are deliberate — never suppress them.
  if (currentUrl.href === nextUrl.href) return defaultShouldRevalidate;

  // Only navigations within the same page (± the dialog child segment) are
  // ours to judge; anything else keeps the default.
  if (
    stripDialogSegment(currentUrl.pathname) !==
    stripDialogSegment(nextUrl.pathname)
  ) {
    return defaultShouldRevalidate;
  }

  const searchChangedBeyondDialog =
    searchIgnoring(currentUrl, ignoredParams) !==
    searchIgnoring(nextUrl, ignoredParams);
  return searchChangedBeyondDialog ? defaultShouldRevalidate : false;
}

/** `/experience/:id/book` and `/experience/:id` are the same page for
 *  revalidation purposes (likewise `…/info/edit` and `…/info`). */
export function stripDialogSegment(pathname: string): string {
  return pathname.replace(/\/(book|edit)$/, '');
}

/** The URL's search string with `ignoredParams` removed, order-normalized so
 *  param reordering doesn't read as a change. */
function searchIgnoring(url: URL, ignoredParams: readonly string[]): string {
  const params = new URLSearchParams(url.search);
  for (const key of ignoredParams) params.delete(key);
  params.sort();
  return params.toString();
}
